/**
 * AskRebeccaCard — Feb 2026
 * =========================
 *
 * Small dashboard tile that surfaces a rotating daily STEM prompt and
 * deep-links the teen into Rebecca's chat with the prompt pre-filled.
 * Turns Rebecca from a passive AvA-library tile into a daily habit
 * hook — one tap and the STEM tutor is ready to answer.
 *
 * Rotation is deterministic on `day-of-year × prompt-count` so every
 * teen sees the same prompt on the same day (community feel), and it
 * never repeats within a full rotation cycle before starting over.
 *
 * The pre-fill message rides through `location.state.prefillMessage`
 * which `ConversationsChat.js` consumes and drops into the input
 * (Send is still the teen's call).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sparkles, GraduationCap, ArrowRight } from 'lucide-react';
import { useAvas } from '../../hooks/useDashboardData';
import { apiClient } from '../../api/client';

// Local fallback — mirrors the backend's `_FALLBACK_PROMPTS` so the tile
// still renders something coherent if the API is unreachable.
const FALLBACK_PROMPTS = [
  "Can you explain photosynthesis in 5 short sentences?",
  "Why is the sky blue during the day but red at sunset?",
  "How does a Wi-Fi signal actually reach my phone?",
  "What's the difference between mass and weight?",
  "Walk me through how a computer runs a single line of code.",
  "Why does hot air rise? Explain like I'm 13.",
  "How do vaccines actually work inside the body?",
  "What is a black hole, and how do we know they exist?",
  "Why do we get goosebumps when we're cold?",
  "How does a plant know which way to grow?",
  "What is entropy in one paragraph?",
  "Explain how DNA copies itself.",
  "How do noise-cancelling headphones work?",
  "Why do we dream — is there a scientific reason?",
  "How does GPS know exactly where I am?",
];

const AskRebeccaCard = () => {
  const navigate = useNavigate();
  const { avas } = useAvas();
  const [dailyPrompt, setDailyPrompt] = useState(null);   // { prompt, source }

  // Fetch today's Claude-generated (or admin-overridden) prompt. Falls
  // back to the deterministic local list only if the API errors.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiClient.request('/api/ask-rebecca/daily');
        if (!cancelled && r?.prompt) {
          setDailyPrompt({ prompt: r.prompt, source: r.source || 'claude' });
        }
      } catch {
        // silent — the useMemo fallback below covers it
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Deterministic day-of-year fallback when the API hasn't returned yet.
  const localFallback = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 0);
    const day = Math.floor((now - start) / (1000 * 60 * 60 * 24));
    return FALLBACK_PROMPTS[day % FALLBACK_PROMPTS.length];
  }, []);

  const todaysPrompt = dailyPrompt?.prompt || localFallback;

  const rebecca = useMemo(
    () => (avas || []).find(a =>
      (a.avatar_config?.preset === 'rebecca_default')
      || (a.name === 'Rebecca' && a.is_fixed)
    ),
    [avas],
  );

  const handleAsk = () => {
    // Deep-link into Rebecca's chat with the prompt in state so
    // `ConversationsChat` can drop it into the input (see the
    // `prefillMessage` useEffect in that file).
    navigate('/conversations', {
      state: {
        selectedAva: rebecca || null,
        prefillMessage: todaysPrompt,
      },
    });
  };

  return (
    <div className="card" data-testid="ask-rebecca-card">
      <div className="card-header">
        <div className="flex items-center gap-2">
          <GraduationCap size={16} className="text-purple-600" />
          <h2 className="card-title">Ask Rebecca</h2>
        </div>
        <Link
          to="/conversations"
          state={{ selectedAva: rebecca || null }}
          className="card-action"
          data-testid="ask-rebecca-open-chat"
        >
          Open chat →
        </Link>
      </div>

      <div
        className="rounded-xl border-2 border-purple-100 bg-gradient-to-br from-purple-50 to-white p-3 mb-3"
        data-testid="ask-rebecca-prompt"
      >
        <div className="flex items-start gap-2">
          <Sparkles size={14} className="text-purple-500 mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-purple-700 mb-1">
              Today&apos;s STEM question
            </div>
            <p className="text-sm text-gray-800 leading-snug">
              {todaysPrompt}
            </p>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={handleAsk}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold shadow transition"
        data-testid="ask-rebecca-cta"
        disabled={!rebecca}
      >
        Ask Rebecca <ArrowRight size={14} />
      </button>

      <p className="text-[11px] text-gray-500 mt-2 text-center">
        New question every day · Rebecca uses the Socratic method — she&apos;ll coach, not just answer.
      </p>
    </div>
  );
};

export default AskRebeccaCard;
